import { Col, Row } from "react-grid-system";
import { CMInfoP2DContact } from "../../../../lib/api/models";
import { convertDateStringToMonthDayYearFormat } from "../../../../lib/formatting";
import StyledButton from "../../../common/buttons/StyledButton";
import Table from "../../../common/tables/Table";
import { CMInfoFormInput, CMInfoSubtool } from "../../CMInfo.hooks";
import Collapsible from "../../../common/Collapsible";
import LabeledCheckbox from "../LabeledCheckbox";
import styles from "./CMInfoP2DSubtool.module.css";
import CMInfoSubtoolButtons from "../CMInfoSubtoolButtons";
import { useIsAgentRolePermissionToDiscuss } from "../../../../lib/roleContext";
import { useState } from "react";

const strings = {
  permissionToDiscuss: "Permission To Discuss",
  lastVerifiedDate: "Last Verified Date",
  nullDate: "XX/XX/XXXX",
  firstName: "First Name",
  lastName: "Last Name",
  relation: "Relation",
  noPermission: "No Permission",
};

type Props = {
  className?: string;
  onShowConfirmationModal: (
    variant: "reset" | "clear",
    onSuccess: () => void,
  ) => void;
  formState: CMInfoFormInput[CMInfoSubtool.P2D];
  initialFormState: CMInfoFormInput[CMInfoSubtool.P2D];
  generalDispatch: (
    payload: Partial<CMInfoFormInput[CMInfoSubtool.P2D]>,
  ) => void;
  onUpdate: () => void;
  currentlyUpdating: CMInfoSubtool | "ALL" | null;
  contacts: CMInfoP2DContact[];
  onOpenPermissionToDiscussTool: () => void;
};

export default function CMInfoP2DSubtool({
  className,
  onShowConfirmationModal,
  formState,
  initialFormState,
  generalDispatch,
  onUpdate,
  currentlyUpdating,
  contacts,
  onOpenPermissionToDiscussTool,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(true);
  const canUseP2D = useIsAgentRolePermissionToDiscuss();

  return (
    <Collapsible
      disabled={
        formState.permissionToDiscussDueForVerification ||
        formState.isPermissionToDiscussVerified
      }
      className={className}
      headerText={strings.permissionToDiscuss}
      isExpanded={isExpanded}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <Row>
        <Col md={8}>
          <Table
            className={styles.p2dTable}
            columns={[
              {
                header: strings.firstName,
                size: 1,
                alignment: "left",
                render: (row) => row.firstName,
              },
              {
                header: strings.lastName,
                size: 1,
                alignment: "left",
                render: (row) => row.lastName,
              },
              {
                header: strings.relation,
                size: 1,
                alignment: "left",
                render: (row) => row.relationship.relationship,
              },
            ]}
            data={contacts}
            emptyTableMessage={strings.noPermission}
          />
        </Col>
        <Col>
          <LabeledCheckbox
            className={styles.p2dLastVerifiedDate}
            label={strings.lastVerifiedDate}
            checkboxLabel={convertDateStringToMonthDayYearFormat(
              formState.permissionToDiscussLastVerifiedDate,
              strings.nullDate,
            )}
            isExpired={formState.permissionToDiscussDueForVerification}
            checked={formState.isPermissionToDiscussVerified}
            onCheck={(isPermissionToDiscussVerified) =>
              generalDispatch({ isPermissionToDiscussVerified })
            }
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <StyledButton
            className={styles.link}
            variant="transparent"
            onClick={() => (canUseP2D ? onOpenPermissionToDiscussTool() : null)}
            disabled={!canUseP2D}
          >
            {strings.permissionToDiscuss}
          </StyledButton>
        </Col>
        <Col className={styles.mt28}>
          <CMInfoSubtoolButtons
            hideScriptButton
            hideClearButton
            onReset={() =>
              onShowConfirmationModal("reset", () =>
                generalDispatch(initialFormState),
              )
            }
            isResetDisabled={!formState.isPermissionToDiscussVerified}
            onUpdate={onUpdate}
            isUpdateDisabled={
              !formState.isPermissionToDiscussVerified || !!currentlyUpdating
            }
            isUpdateLoading={currentlyUpdating === CMInfoSubtool.P2D}
          />
        </Col>
      </Row>
    </Collapsible>
  );
}
